// src/components/desk/FeatureCapture.jsx — every registry feature the property can carry, pickable
// from the add/edit-property form (owner 2026-09-16: "all the fields known to our system").
//
// Draws only. Which codes appear, what an answer sends, and what each view shows live in
// src/lib/featureCapture.js and are tested there. Its own file so it can be mounted outside the
// login gate, like BuildingFacts.
//
// Redesign stage 2 (from the mockup): an answered count in the header, three views — unanswered
// first · AI-filled · disagreeing — and per row yes / no / clear, with AI and disagreement marks.
import { useEffect, useMemo, useState } from 'react'
import { t, tOr, tLog, getLanguage } from '../../lib/i18n.js'
import { pill } from './primitives.jsx'
import { captureFeatureGroups, featureMatches, answerModeOf, featureView, MAX_CHIP_ANSWERS } from '../../lib/featureCapture.js'

const SELECTED_YES = 'bg-green-600/20 border-green-600 text-green-600 dark:text-green-400'
const SELECTED_NO = 'bg-rose-600/15 border-rose-500 text-rose-600 dark:text-rose-400'
const IDLE = 'border-border text-muted-foreground hover:bg-muted'

export function featureLabel(f) {
  const lang = getLanguage()
  if (lang === 'ka') return f.name_ka || f.name_en || f.code
  if (lang === 'ru') return f.name_ru || f.name_en || f.name_ka || f.code
  return f.name_en || f.name_ka || f.code
}

export function FeatureCapture({ vocab, kind, kindCaps, exclude, answers, setAnswers, pendingCount = 0, aiCodes = null, disagree = null }) {
  const [q, setQ] = useState('')
  const [mode, setMode] = useState('unanswered')
  const [openGroups, setOpenGroups] = useState(() => new Set())
  const groups = useMemo(() => captureFeatureGroups(vocab, kind, kindCaps, exclude), [vocab, kind, kindCaps, exclude])
  const searching = q.trim().length > 0
  // A paste that answered features opens the groups holding them, so the agent reviews what the AI
  // filled instead of saving answers they never saw.
  useEffect(() => {
    if (!aiCodes || !aiCodes.size) return
    const hit = groups.filter(([, rows]) => rows.some(f => aiCodes.has(f.code))).map(([g]) => g)
    if (hit.length) setOpenGroups(s => new Set([...s, ...hit]))
  }, [aiCodes, groups])
  const set = (code, v) => setAnswers(a => {
    const next = { ...a }
    if (v === '' || next[code] === v) delete next[code]; else next[code] = v
    return next
  })
  if (!groups.length) return null

  const allRows = groups.flatMap(([, rows]) => rows)
  const isAnswered = code => answers[code] === 'true' || answers[code] === 'false'
  const answeredTotal = allRows.filter(f => isAnswered(f.code)).length
  const aiCount = allRows.filter(f => aiCodes && aiCodes.has(f.code)).length
  const disagreeCount = allRows.filter(f => disagree && disagree.has(f.code)).length
  const shown = featureView(groups, answers, mode, { aiCodes, disagree })
  const tabs = [
    ['unanswered', t('desk_feature_view_unanswered'), null],
    ['ai', t('desk_feature_view_ai'), aiCount],
    ['disagreeing', t('desk_feature_view_disagreeing'), disagreeCount],
  ]

  return (
    <div className="mt-4 rounded-xl border border-border" data-feature-capture>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <p className="text-xs font-semibold text-muted-foreground">{t('desk_feature_capture_title')}</p>
        <span className="rounded-full bg-muted px-1.5 text-[11px] leading-5 tabular-nums text-muted-foreground" data-feature-answered>{answeredTotal}/{allRows.length}</span>
        <input id="feature-capture-search" value={q} onChange={e => setQ(e.target.value)} placeholder={t('desk_feature_capture_search')}
          className="sm:ml-auto w-full sm:w-60 rounded-lg border border-border bg-background px-2 py-1 text-sm" />
      </div>
      <div className="flex flex-wrap gap-1.5 px-3 pt-2" role="tablist">
        {tabs.map(([m, label, n]) => (
          <button key={m} type="button" role="tab" aria-selected={mode === m} data-feature-view={m}
            disabled={n === 0 && m !== 'unanswered'}
            onClick={() => setMode(m)}
            className={pill('none', { door: true, extra: `${mode === m ? 'bg-foreground text-background border-foreground' : IDLE} disabled:opacity-40` })}>
            {label}{n != null && <span className="tabular-nums"> · {n}</span>}
          </button>
        ))}
      </div>
      {pendingCount > MAX_CHIP_ANSWERS && (
        <p className="px-3 pt-2 text-xs text-amber-600 dark:text-amber-400" data-feature-capture-cap>
          {tLog('desk_feature_capture_cap', { n: MAX_CHIP_ANSWERS })}
        </p>
      )}
      <div className="p-2 space-y-0.5">
        {shown.map(([group, rows]) => {
          const list = searching ? rows.filter(f => featureMatches(f, q)) : rows
          if (!list.length) return null
          const groupRows = (groups.find(([g]) => g === group) || [null, rows])[1]
          const answered = groupRows.filter(f => isAnswered(f.code)).length
          const open = searching || mode !== 'unanswered' || openGroups.has(group)
          return (
            <div key={group} className="rounded-lg" data-feature-group={group}>
              <button type="button" className="w-full flex items-center justify-between px-2 py-1.5 text-[13px] text-foreground hover:bg-muted rounded-lg"
                aria-expanded={open}
                onClick={() => setOpenGroups(s => { const n = new Set(s); n.has(group) ? n.delete(group) : n.add(group); return n })}>
                <span>{tOr('desk_feature_capture_group_' + group, tOr('desk_facet_features_' + group, group.replace(/_/g, ' ')))}</span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
                  <span className="rounded-full bg-muted px-1.5 leading-5">{answered}/{groupRows.length}</span>
                  <span aria-hidden>{open ? '▾' : '▸'}</span>
                </span>
              </button>
              {open && (
                <ul className="px-2 pb-2">
                  {list.map(f => {
                    const v = answers[f.code] || ''
                    const yesOnly = answerModeOf(f) === 'yes_only'
                    return (
                      <li key={f.code} className="flex items-center gap-1.5 flex-wrap py-1 border-b border-border/40 last:border-0" data-feature={f.code}>
                        <span className="text-[13px] flex-1 min-w-[8rem]" title={f.description_ka || f.description_en || f.code}>
                          {featureLabel(f)}
                          {aiCodes && aiCodes.has(f.code) && <span className="ml-1.5 rounded-full border border-sky-500/50 bg-sky-500/10 px-1.5 text-[10px] leading-4 text-sky-600 dark:text-sky-400" data-feature-ai>{t('desk_feature_capture_ai')}</span>}
                          {disagree && disagree.has(f.code) && <span className="ml-1.5 rounded-full border border-amber-500/50 bg-amber-500/10 px-1.5 text-[10px] leading-4 text-amber-700 dark:text-amber-300" data-feature-disagree>⚠ {t('desk_feature_disagree_mark')}</span>}
                        </span>
                        <button type="button" aria-pressed={v === 'true'} onClick={() => set(f.code, 'true')}
                          className={pill('none', { door: true, extra: v === 'true' ? SELECTED_YES : IDLE })}>{t('desk_facts_yes')}</button>
                        {!yesOnly && (
                          <button type="button" aria-pressed={v === 'false'} onClick={() => set(f.code, 'false')}
                            className={pill('none', { door: true, extra: v === 'false' ? SELECTED_NO : IDLE })}>{t('desk_facts_no')}</button>
                        )}
                        <button type="button" aria-label={t('desk_tristate_not_stated')} title={t('desk_tristate_not_stated')} onClick={() => set(f.code, '')}
                          className={pill('none', { door: true, extra: `border-dashed ${v === '' ? 'border-muted-foreground/60 text-foreground' : IDLE}` })}>—</button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )
        })}
        {!shown.length && <p className="px-2 py-3 text-xs text-muted-foreground">{t('desk_feature_view_empty')}</p>}
      </div>
      <p className="px-3 pb-2 text-[11px] text-muted-foreground">{t('desk_feature_capture_hint')}</p>
    </div>
  )
}
